import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Box, Card, Stack, Button, Container, Typography, CardContent } from '@mui/material';
import { RefreshOutlined, BugReportOutlined } from '@mui/icons-material';
import { trackSessionHealth } from '@/instrument';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  eventId?: string;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Track session as crashed for Sentry crash free sessions
    trackSessionHealth('crashed');

    // Capture the error with Sentry and get the event ID
    const eventId = Sentry.captureException(error, {
      tags: {
        component: 'ErrorBoundary',
        sessionStatus: 'crashed',
      },
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      extra: {
        errorInfo,
        errorBoundary: true,
      },
    });

    this.setState({
      eventId,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReportFeedback = () => {
    const { eventId } = this.state;
    if (eventId) {
      Sentry.showReportDialog({ eventId });
    }
  };

  render() {
    const { hasError, error, eventId } = this.state;
    const { fallback, showDetails, children } = this.props;

    if (hasError) {
      // Custom fallback UI
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <Container maxWidth="md" sx={{ mt: 8 }}>
          <Card>
            <CardContent>
              <Stack spacing={3} alignItems="center" textAlign="center">
                <BugReportOutlined
                  sx={{
                    fontSize: 64,
                    color: 'error.main',
                    opacity: 0.8,
                  }}
                />

                <Typography variant="h4" component="h1">
                  ¡Oops! Algo salió mal
                </Typography>

                <Typography variant="body1" color="text.secondary" maxWidth="sm">
                  Se ha producido un error inesperado en la aplicación. Nuestro equipo ha sido
                  notificado automáticamente.
                </Typography>

                {showDetails && error && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      maxWidth: '100%',
                      overflow: 'auto',
                    }}
                  >
                    <Typography variant="caption" color="error" component="pre">
                      {error.message}
                    </Typography>
                  </Box>
                )}

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={<RefreshOutlined />}
                    onClick={this.handleReload}
                    size="large"
                  >
                    Recargar página
                  </Button>

                  {eventId && (
                    <Button variant="outlined" onClick={this.handleReportFeedback} size="large">
                      Reportar problema
                    </Button>
                  )}
                </Stack>

                {eventId && (
                  <Typography variant="caption" color="text.secondary">
                    ID del error: {eventId}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Container>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
